/**
 * Autosaves of unsaved work, from this device (IndexedDB) and from the account (server), merged
 * into the list the recovery banner offers.
 */
import type { RecoveryEntry } from '@elec/platform';
import type { AutosaveSummary } from '@elec/sync';

/** Local copy every 3 s after a change; account copy at most every 15 s while editing. */
export const AUTOSAVE_MS = 3000;
export const ACCOUNT_AUTOSAVE_MS = 15_000;

export interface AutosaveCandidate {
  id: string;
  name: string;
  savedAt: number;
  /** Where the newest copy is; bytes are present for copies on this device. */
  where: 'device' | 'account';
  bytes?: Uint8Array;
}

/**
 * One candidate per project, the newest copy wins (this device's on a tie: no download).
 * Newest first; `exclude` (the open project) is left out.
 */
export function mergeAutosaves(
  local: RecoveryEntry[],
  remote: AutosaveSummary[],
  exclude?: string,
): AutosaveCandidate[] {
  const by = new Map<string, AutosaveCandidate>();
  for (const r of remote)
    by.set(r.projectId, { id: r.projectId, name: r.name, savedAt: r.savedAt, where: 'account' });
  for (const l of local) {
    const cur = by.get(l.id);
    if (!cur || l.savedAt >= cur.savedAt)
      by.set(l.id, { id: l.id, name: l.name, savedAt: l.savedAt, where: 'device', bytes: l.bytes });
  }
  return [...by.values()].filter((c) => c.id !== exclude).sort((a, b) => b.savedAt - a.savedAt);
}

/** Copies on this device that the account lacks or has an older version of: to upload. */
export function newerOnDevice(local: RecoveryEntry[], remote: AutosaveSummary[]): RecoveryEntry[] {
  const at = new Map(remote.map((r) => [r.projectId, r.savedAt]));
  return local.filter((l) => l.id !== 'current' && l.savedAt > (at.get(l.id) ?? 0));
}
